/* eslint-env es2020 */
// zpost-app/src/components/ensapp/AvatarManager.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { namehash } from 'ethers';
import { useAuth } from '../../contexts/AuthContext';
import useBridgeTransaction from '../../hooks/useBridgeTransaction';
import { getRPCProvider } from '../../utils/rpc';

const PUBLIC_RESOLVER_ADDRESS = '0x231b0Ee14048e9dCcD1d247744d114a4EB5E8E63';

const AvatarManager = ({ identityAuth }) => {
  const { walletAddress, ensName, isAuthenticated } = identityAuth || {};

  // --- State ---
  const [provider, setProvider] = useState(null);
  const [providerReady, setProviderReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Domains list & selected domain
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');

  // Avatar & header records (raw and processed)
  const [avatar, setAvatar] = useState({ raw: '', url: '' });
  const [header, setHeader] = useState({ raw: '', url: '' });
  const [avatarError, setAvatarError] = useState(false);
  const [headerError, setHeaderError] = useState(false);

  // File upload & preview
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [selectedHeaderFile, setSelectedHeaderFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState('');
  const [manualHeaderUrl, setManualHeaderUrl] = useState('');

  // Transaction state
  const { sendTransaction: bridgeSend } = useBridgeTransaction();
  const { wallet: authWallet, connectToZwallet } = useAuth();
  const authWalletRef = useRef(authWallet);
  const [receiptTxHash, setReceiptTxHash] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs for file inputs
  const avatarFileInput = useRef(null);
  const headerFileInput = useRef(null);

  // Keep authWallet ref updated
  useEffect(() => {
    authWalletRef.current = authWallet;
  }, [authWallet]);

  // --- RPC Provider Init ---
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const p = await getRPCProvider();
        if (!cancelled) {
          setProvider(p);
          setProviderReady(true);
        }
      } catch (err) {
        setError('Failed to connect to Ethereum network.');
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  // --- Helper: resolve image URI (same as WhoIsWho) ---
  const resolveImageUri = useCallback((uri, domain) => {
    if (!uri) return '';

    if (uri.startsWith('ipfs://')) {
      const hash = uri.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${hash}`;
    }

    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      return uri;
    }

    if (uri.includes('eip155:')) {
      return `https://metadata.ens.domains/mainnet/avatar/${domain}`;
    }

    // Fallback: treat as direct URL (e.g., euc.li)
    return uri;
  }, []);

  // --- Fetch domains owned by the wallet (via proxy) ---
  const fetchDomains = useCallback(async () => {
    if (!walletAddress || !provider) return;
    try {
      const query = `{ domains(where: { owner: "${walletAddress.toLowerCase()}" }) { name } }`;
      const res = await fetch('/api/ens/subgraph', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const json = await res.json();
      let names = (json.data?.domains || []).map(d => d.name);

      // Always include the primary ENS name if available and not already present
      if (ensName && !names.includes(ensName)) {
        names = [ensName, ...names];
      }

      setDomains(names);

      // Select the first domain, or the primary ENS name if none found
      if (names.length > 0) {
        setSelectedDomain(names[0]);
        await loadRecords(names[0]);
      } else {
        setSelectedDomain('');
        setAvatar({ raw: '', url: '' });
        setHeader({ raw: '', url: '' });
      }
    } catch (err) {
      console.error('Domain fetch failed:', err);
      setError('Could not load your ENS domains.');
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress, provider, ensName]);

  // --- Load avatar & header records for a domain ---
  const loadRecords = useCallback(async (domain) => {
    if (!provider || !domain) return;
    try {
      const resolver = await provider.getResolver(domain);
      if (!resolver) {
        setAvatar({ raw: '', url: '' });
        setHeader({ raw: '', url: '' });
        return;
      }

      const [rawAvatar, rawHeader] = await Promise.all([
        resolver.getText('avatar').catch(() => ''),
        resolver.getText('header').catch(() => '')
      ]);

      setAvatar({
        raw: rawAvatar,
        url: rawAvatar ? resolveImageUri(rawAvatar, domain) : ''
      });
      setHeader({
        raw: rawHeader,
        url: rawHeader ? resolveImageUri(rawHeader, domain) : ''
      });
      setAvatarError(false);
      setHeaderError(false);
    } catch (err) {
      console.error('Error loading records:', err);
      setError('Failed to fetch avatar/header records.');
    }
  }, [provider, resolveImageUri]);

  // --- Initial load ---
  useEffect(() => {
    if (provider && walletAddress) {
      fetchDomains();
    } else {
      setIsLoading(false);
    }
  }, [provider, walletAddress, fetchDomains]);

  // --- Handle domain change ---
  const handleDomainChange = async (domain) => {
    setSelectedDomain(domain);
    setAvatarPreview('');
    setHeaderPreview('');
    setManualHeaderUrl('');
    setSelectedAvatarFile(null);
    setSelectedHeaderFile(null);
    if (avatarFileInput.current) avatarFileInput.current.value = '';
    if (headerFileInput.current) headerFileInput.current.value = '';
    await loadRecords(domain);
  };

  // --- Image selection & preview (avatar) ---
  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB.');
      return;
    }
    setSelectedAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  // --- Image selection & preview (header) ---
  const handleHeaderSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB.');
      return;
    }
    setSelectedHeaderFile(file);
    setHeaderPreview(URL.createObjectURL(file));
  };

  // --- IPFS Upload (Pinata) ---
  const uploadToIPFS = async (file) => {
    const PINATA_JWT = process.env.REACT_APP_PINATA_JWT;
    if (!PINATA_JWT) throw new Error('IPFS service not configured.');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('pinataMetadata', JSON.stringify({
      name: `avatar-${selectedDomain}-${Date.now()}`,
      keyvalues: { type: 'avatar', domain: selectedDomain }
    }));
    const res = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
      method: 'POST',
      headers: { Authorization: `Bearer ${PINATA_JWT}` },
      body: formData
    });
    if (!res.ok) throw new Error('IPFS upload failed');
    const data = await res.json();
    return `ipfs://${data.IpfsHash}`;
  };

  // --- Generic transaction sender (bridge) ---
  const sendRecordTransaction = useCallback(async (key, value) => {
    if (!selectedDomain) throw new Error('No domain selected');
    if (!walletAddress) throw new Error('Wallet not connected');

    // Verify ownership
    const resolved = await provider.resolveName(selectedDomain);
    if (!resolved || resolved.toLowerCase() !== walletAddress.toLowerCase()) {
      throw new Error('You do not own this domain.');
    }

    const resolver = await provider.getResolver(selectedDomain);
    if (!resolver) throw new Error('No resolver configured for this domain.');

    const node = namehash(selectedDomain);
    const { Interface } = await import('ethers');
    const iface = new Interface(['function setText(bytes32,string,string)']);
    const data = iface.encodeFunctionData('setText', [node, key, value]);
    const tx = {
      to: PUBLIC_RESOLVER_ADDRESS,
      data,
      value: '0x0',
      from: walletAddress
    };
    const result = await bridgeSend(tx);
    setReceiptTxHash({ hash: result.hash, blockNumber: result.blockNumber });
    return result;
  }, [selectedDomain, walletAddress, provider, bridgeSend]);

  // --- Wait for wallet unlock if needed ---
  const ensureWalletUnlocked = async () => {
    if (authWalletRef.current?.isConnected) return true;
    connectToZwallet();
    for (let i = 0; i < 30; i++) {
      await new Promise(r => setTimeout(r, 1000));
      if (authWalletRef.current?.isConnected) return true;
    }
    return false;
  };

  // --- Handle Set Avatar (upload + transaction) ---
  const handleSetAvatar = async () => {
    if (!selectedAvatarFile) {
      setError('Please select an image first.');
      return;
    }
    if (!await ensureWalletUnlocked()) {
      setError('Wallet unlock timed out.');
      return;
    }
    setIsProcessing(true);
    setError('');
    setSuccess('');
    try {
      setSuccess('Uploading to IPFS...');
      const ipfsUri = await uploadToIPFS(selectedAvatarFile);
      setSuccess('Sending transaction...');
      await sendRecordTransaction('avatar', ipfsUri);
      setSuccess('Avatar updated!');
      await loadRecords(selectedDomain);
      setSelectedAvatarFile(null);
      setAvatarPreview('');
      if (avatarFileInput.current) avatarFileInput.current.value = '';
    } catch (err) {
      setError(err.message || 'Failed to set avatar.');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handle Set Header (upload + transaction) ---
  const handleSetHeader = async () => {
    if (!selectedHeaderFile) {
      setError('Please select an image first.');
      return;
    }
    if (!await ensureWalletUnlocked()) {
      setError('Wallet unlock timed out.');
      return;
    }
    setIsProcessing(true);
    setError('');
    setSuccess('');
    try {
      setSuccess('Uploading to IPFS...');
      const ipfsUri = await uploadToIPFS(selectedHeaderFile);
      setSuccess('Sending transaction...');
      await sendRecordTransaction('header', ipfsUri);
      setSuccess('Header updated!');
      await loadRecords(selectedDomain);
      setSelectedHeaderFile(null);
      setHeaderPreview('');
      if (headerFileInput.current) headerFileInput.current.value = '';
    } catch (err) {
      setError(err.message || 'Failed to set header.');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handle Set Header via manual URL ---
  const handleSetHeaderUrl = async () => {
    if (!manualHeaderUrl.trim()) {
      setError('Please enter a URL.');
      return;
    }
    if (!await ensureWalletUnlocked()) {
      setError('Wallet unlock timed out.');
      return;
    }
    setIsProcessing(true);
    setError('');
    setSuccess('');
    try {
      setSuccess('Sending transaction...');
      await sendRecordTransaction('header', manualHeaderUrl.trim());
      setSuccess('Header updated!');
      await loadRecords(selectedDomain);
      setManualHeaderUrl('');
    } catch (err) {
      setError(err.message || 'Failed to set header.');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handle Remove Avatar ---
  const handleRemoveAvatar = async () => {
    if (!await ensureWalletUnlocked()) {
      setError('Wallet unlock timed out.');
      return;
    }
    setIsProcessing(true);
    setError('');
    setSuccess('');
    try {
      setSuccess('Removing avatar...');
      await sendRecordTransaction('avatar', '');
      setSuccess('Avatar removed.');
      await loadRecords(selectedDomain);
    } catch (err) {
      setError(err.message || 'Failed to remove avatar.');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handle Remove Header ---
  const handleRemoveHeader = async () => {
    if (!await ensureWalletUnlocked()) {
      setError('Wallet unlock timed out.');
      return;
    }
    setIsProcessing(true);
    setError('');
    setSuccess('');
    try {
      setSuccess('Removing header...');
      await sendRecordTransaction('header', '');
      setSuccess('Header removed.');
      await loadRecords(selectedDomain);
    } catch (err) {
      setError(err.message || 'Failed to remove header.');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Error fallback for images ---
  const handleAvatarImageError = () => {
    if (!avatar.url) return;
    setAvatarError(true);
    // Optionally try alternative gateways if needed
  };

  const handleHeaderImageError = () => {
    if (!header.url) return;
    setHeaderError(true);
  };

  // --- Render ---
  if (!identityAuth) {
    return (
      <div className="content-card">
        <h3>Avatar & Header Management</h3>
        <p style={{ color: '#ff4444' }}>Authentication required.</p>
      </div>
    );
  }

  if (isLoading || !providerReady) {
    return (
      <div className="content-card">
        <h3>Avatar & Header Management</h3>
        <p>Loading your ENS domains...</p>
      </div>
    );
  }

  return (
    <div className="content-card">
      <h3>Avatar & Header Management</h3>
      <p className="text-secondary">
        Set a profile picture (avatar) and a banner image (header) for your ENS name.
        Both will be visible across Web3 applications.
      </p>

      {/* Domain selector */}
      {domains.length > 0 ? (
        <div style={{ marginBottom: '2rem' }}>
          <label className="form-label">Select Domain</label>
          <select
            value={selectedDomain}
            onChange={(e) => handleDomainChange(e.target.value)}
            className="domain-input"
            style={{ width: '100%', padding: '10px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
          >
            {domains.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
      ) : (
        <p className="text-secondary">No ENS domains found for your wallet.</p>
      )}

      {selectedDomain && (
        <>
          {/* ===== AVATAR SECTION ===== */}
          <div style={{ marginBottom: '3rem' }}>
            <h4 style={{ color: 'var(--gold)' }}>Avatar (Profile Picture)</h4>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '2rem', background: 'rgba(255,215,0,0.05)', borderRadius: '12px',
              border: '2px dashed var(--gold)', marginBottom: '1rem'
            }}>
              {avatarPreview || avatar.url ? (
                <img
                  src={avatarPreview || avatar.url}
                  alt="Avatar"
                  style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--gold)' }}
                  onError={handleAvatarImageError}
                />
              ) : (
                <div style={{ width: '150px', height: '150px', borderRadius: '50%', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span>No avatar</span>
                </div>
              )}
              <p style={{ marginTop: '1rem' }}>
                {avatarPreview ? 'Preview' : avatar.url ? 'Current avatar' : 'No avatar set'}
              </p>
              {avatar.raw && !avatarPreview && (
                <p style={{ fontSize: '0.8rem', wordBreak: 'break-all', color: 'var(--gold)' }}>
                  {avatar.raw}
                </p>
              )}
            </div>

            <input type="file" ref={avatarFileInput} style={{ display: 'none' }} onChange={handleAvatarSelect} accept="image/*" />
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <button onClick={() => avatarFileInput.current.click()} className="btn btn-dark-red" disabled={isProcessing}>
                Choose Image
              </button>
              <button onClick={handleSetAvatar} disabled={!selectedAvatarFile || isProcessing} className="btn btn-gold">
                {isProcessing ? 'Processing...' : 'Set Avatar'}
              </button>
              {avatar.url && (
                <button onClick={handleRemoveAvatar} disabled={isProcessing} className="btn btn-dark-red">
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* ===== HEADER SECTION ===== */}
          <div style={{ marginBottom: '3rem' }}>
            <h4 style={{ color: 'var(--gold)' }}>Header (Banner Image)</h4>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '2rem', background: 'rgba(255,215,0,0.05)', borderRadius: '12px',
              border: '2px dashed var(--gold)', marginBottom: '1rem'
            }}>
              {headerPreview || header.url ? (
                <img
                  src={headerPreview || header.url}
                  alt="Header"
                  style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', border: '2px solid var(--gold)' }}
                  onError={handleHeaderImageError}
                />
              ) : (
                <div style={{ width: '100%', height: '150px', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span>No header</span>
                </div>
              )}
              <p style={{ marginTop: '1rem' }}>
                {headerPreview ? 'Preview' : header.url ? 'Current header' : 'No header set'}
              </p>
              {header.raw && !headerPreview && (
                <p style={{ fontSize: '0.8rem', wordBreak: 'break-all', color: 'var(--gold)' }}>
                  {header.raw}
                </p>
              )}
            </div>

            {/* Upload via file */}
            <input type="file" ref={headerFileInput} style={{ display: 'none' }} onChange={handleHeaderSelect} accept="image/*" />
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <button onClick={() => headerFileInput.current.click()} className="btn btn-dark-red" disabled={isProcessing}>
                Choose Image
              </button>
              <button onClick={handleSetHeader} disabled={!selectedHeaderFile || isProcessing} className="btn btn-gold">
                {isProcessing ? 'Processing...' : 'Set Header (Upload)'}
              </button>
            </div>

            {/* Or manual URL */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <input
                type="text"
                placeholder="https://... or ipfs://..."
                value={manualHeaderUrl}
                onChange={(e) => setManualHeaderUrl(e.target.value)}
                className="domain-input"
                style={{ flex: 1, padding: '8px' }}
              />
              <button onClick={handleSetHeaderUrl} disabled={!manualHeaderUrl || isProcessing} className="btn btn-gold">
                Set URL
              </button>
              {header.url && (
                <button onClick={handleRemoveHeader} disabled={isProcessing} className="btn btn-dark-red">
                  Remove
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Status messages */}
      {error && <div className="status-message status-error">{error}</div>}
      {success && <div className="status-message status-success">{success}</div>}

      {/* Info box */}
      <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(255,215,0,0.1)', borderRadius: '8px', border: '1px solid var(--gold)' }}>
        <h4 style={{ color: 'var(--gold)' }}>Requirements</h4>
        <ul style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          <li>Images: JPEG, PNG, GIF, SVG – max 5MB.</li>
          <li>Square images for avatar, rectangular (approx 3:1) for header.</li>
          <li>Stored on IPFS via Pinata.</li>
          <li>Transactions are broadcast via the ZPost bridge and require confirmation.</li>
        </ul>
      </div>

      {/* Receipt Modal */}
      {receiptTxHash && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000
        }}>
          <div style={{
            background: 'var(--card-bg)', padding: '30px', borderRadius: '15px',
            border: '2px solid var(--gold)', maxWidth: '500px', width: '100%'
          }}>
            <h3 style={{ color: 'var(--gold)', textAlign: 'center' }}>Transaction Confirmed</h3>
            <p style={{ color: 'var(--text-secondary)' }}>Block: {receiptTxHash.blockNumber}</p>
            <div style={{ wordBreak: 'break-all', background: 'rgba(255,215,0,0.1)', padding: '10px', borderRadius: '5px' }}>
              <a href={`https://etherscan.io/tx/${receiptTxHash.hash}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>
                {receiptTxHash.hash}
              </a>
            </div>
            <button onClick={() => setReceiptTxHash(null)} style={{ marginTop: '20px', width: '100%', background: 'transparent', color: '#ff4444', border: '1px solid #ff4444', padding: '10px', borderRadius: '8px' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarManager;
